import { observable, action, makeObservable } from "mobx";
import { create, persist } from "mobx-persist";
import { lang } from "../lang";
import { DownloadFile, Log, MenuItem, UploadRowItem, Workbook, Notice } from "../interface";
import { reloadApi } from "../api";
import { outputTables } from "../data/outputTables";
import * as HelperSrc from "../HelperSrc";

export type UserStore = {
    id?: number;
    username?: string;
    is_staff?: boolean;
    is_superuser?: boolean;
    is_support?: boolean;
    groupnames?: string[];
    last_activity?: string;
};

export enum ALERT_TYPE {
    ERROR = "error",
    SUCCESS = "success",
    ALERT = "alert"
}

export type Alert = {
    type: ALERT_TYPE;
    message: string;
    mode?: string;
};

class Store {
    @observable public isAD = false;

    @persist @observable public permission = "";
    @persist @observable public db: string | null = null;
    @persist("object") @observable public user: UserStore = {};
    @persist @observable public country = "jp";
    @persist @observable public language = "ja";
    @persist @observable public sidebar = "";
    @persist @observable public sessionExpired = false;

    @observable public workbooks: Workbook[] = [];
    @observable public workbookCount = 0;
    @observable public secondaryWorkbooks: Workbook[] = [];
    @observable public secondaryWorkbookCount = 0;

    // Admin only data
    @observable public auditLogs: Log[] = [];

    @observable public toUpload: UploadRowItem[] = [];
    @observable public waitingDownload: DownloadFile[] = [];
    @observable public downloadingFiles: DownloadFile[] = [];

    // Data specific to a workbook
    @observable public currentTable: MenuItem = outputTables[0];
    @observable public editing = false;

    // Shows in the bottom right for 10 seconds
    @observable public notice: Notice = {
        time: 0,
        message: "",
        fileList: []
    };
    @observable public showNotice = false;

    @observable public alert: Alert = {
        type: ALERT_TYPE?.ALERT,
        message: "",
        mode: ""
    };

    constructor() {
        makeObservable(this);
    }

    @action
    selectClient(db: string, permissions: string) {
        this.db = db;
        this.permission = permissions;
    }

    @action
    setDB(value: string | null) {
        this.db = value;
    }

    @action
    setUser(user: UserStore) {
        this.user = user;
    }

    @action
    setIsAD(isAD: boolean) {
        this.isAD = isAD;
    }

    @action
    doNotice(message: string, isSuccess = false, timeDifference = 3000, callback?: () => void, fileList: string[] = []) {
        const elementId = document.querySelector<HTMLElement>("#noticebox");
        if (isSuccess) {
            if (elementId) {
                elementId.classList.add("alert-success");
                elementId.classList.remove("alert-false");
            }
        } else {
            if (elementId) {
                elementId.classList.add("alert-false");
                elementId.classList.remove("alert-success");
            }
        }

        const noticeTimer = () => {
            if (Date.now() - store.notice.time > timeDifference) {
                store.setShowNotice(false);
                store.setNotice({
                    time: 0,
                    message: "",
                    fileList: []
                });

                if (callback) {
                    callback();
                }
            } else {
                setTimeout(noticeTimer, 1000);
            }
        };
        store.setNotice({
            time: Date.now(),
            message,
            fileList
        });
        store.setShowNotice(true);
        setTimeout(noticeTimer, 1000);
    }

    @action
    setAlertData(value: Alert) {
        this.alert = value;
    }

    @action
    setCountry(country: string) {
        this.country = country;
    }

    @action
    setLanguage(language: string) {
        this.language = language;
    }

    @action
    setShowNotice(value: boolean) {
        this.showNotice = value;
    }

    @action
    setWaitingDownload(value: DownloadFile[]) {
        this.waitingDownload = value;
    }

    @action
    setWaitingFileStatus(file: DownloadFile, status: string) {
        file.status = status;
        return file;
    }

    @action
    setDownloadingFile(fileList: DownloadFile[]) {
        this.downloadingFiles = fileList;
    }

    @action
    setWorkbooks(workbooks: Workbook[]) {
        this.workbooks = workbooks;
    }

    @action
    setWorkbookCount(value: number) {
        this.workbookCount = value;
    }

    @action
    setSecondaryWorkbooks(workbooks: Workbook[]) {
        this.secondaryWorkbooks = workbooks;
    }

    @action
    setSecondaryWorkbookCount(value: number) {
        this.secondaryWorkbookCount = value;
    }

    @action
    setToUpload(value: UploadRowItem[]) {
        this.toUpload = value;
    }

    @action
    setCurrentTable(value: MenuItem) {
        this.currentTable = value;
    }

    @action
    setEditing(value: boolean) {
        this.editing = value;
    }

    @action
    setNotice(value: Notice) {
        this.notice = value;
    }

    @action
    clearSession() {
        this.sessionExpired = false;
        this.db = "";
        this.permission = "";
        this.user = {};
    }

    @action
    setSidebar(value: string) {
        this.sidebar = value;
    }

    @action
    setIsSessionExpired(value: boolean) {
        this.sessionExpired = value;
    }
}

const hydrate = create({});
const store = new Store();

HelperSrc.setHelperStore(store);

hydrate(`nbb_primaryStore`, store)
    .then(() => {
        lang.setLanguage(store.language);
        reloadApi();
    })
    .catch((err: Error) => {
        store.doNotice(err.message);
    });

export { store };
